import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client.js'
import { RouteCard, type RouteCardRoute } from '../components/RouteCard.js'
import { useLocale } from '../locale/LocaleContext.js'
import { SELECTABLE_ACTIVITIES } from '../lib/selectableActivities.js'

export function LandingPage() {
  const { t } = useLocale()
  const [routes, setRoutes] = useState<RouteCardRoute[] | null>(null)
  const [routeError, setRouteError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .listRoutes({
        status: 'published',
        limit: 4,
        includeSeasonality: true,
        includeThumbnail: true,
      })
      .then((items) => {
        if (cancelled) return
        setRoutes(items)
        setRouteError(null)
      })
      .catch((err) => {
        if (cancelled) return
        setRoutes([])
        setRouteError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [])

  const stepKeys = useMemo(() => ['discover', 'inspect', 'carry'] as const, [])

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--xr-bg-canvas)]">
      <section className="relative isolate min-h-[460px] overflow-hidden bg-[#123d31]">
        <img
          src="/hero-bg.jpg"
          alt=""
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#102f27]/95 via-[#123d31]/82 to-[#123d31]/35" />
        <div className="absolute inset-x-0 bottom-0 -z-10 h-36 bg-gradient-to-t from-[#123d31]/70 to-transparent" />

        <div className="xr-page-container flex min-h-[460px] items-end px-4 py-12 md:items-center md:px-8 md:py-16">
          <div className="max-w-3xl">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-white/70">
              {t('landing.eyebrow')}
            </p>
            <h1 className="max-w-2xl text-4xl font-semibold leading-[1.08] tracking-[-0.035em] text-white md:text-6xl">
              {t('landing.title')}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-white/82 md:text-lg">
              {t('landing.subtitle')}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/discover"
                className="inline-flex h-11 items-center rounded-[var(--xr-radius-full)] bg-white px-5 text-sm font-semibold text-[#1a5a44] shadow-sm transition hover:bg-white/90"
              >
                {t('landing.primaryCta')}
              </Link>
              <span className="hidden h-5 w-px bg-white/25 sm:block" aria-hidden="true" />
              {SELECTABLE_ACTIVITIES.map((key) => (
                <Link
                  key={key}
                  to={`/discover?activity=${key}`}
                  className="inline-flex h-10 items-center rounded-[var(--xr-radius-full)] border border-white/30 bg-black/10 px-4 text-sm font-medium text-white transition hover:border-white/70 hover:bg-black/20"
                >
                  {t(`landing.activity.${key}`)}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="xr-page-container px-4 py-10 md:px-8 md:py-14">
        <div className="flex flex-col gap-4 border-b border-[var(--xr-border-default)] pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--xr-brand-primary)]">
              {t('landing.routes.eyebrow')}
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-[var(--xr-text-primary)] md:text-3xl">
              {t('landing.routes.title')}
            </h2>
            <p className="mt-3 text-sm leading-6 text-[var(--xr-text-secondary)]">
              {t('landing.routes.body')}
            </p>
          </div>
          <Link
            to="/discover"
            className="inline-flex h-10 flex-none items-center self-start rounded-[var(--xr-radius-full)] border border-[var(--xr-border-strong)] px-4 text-sm font-semibold text-[var(--xr-text-primary)] transition hover:border-[var(--xr-brand-primary)] hover:text-[var(--xr-brand-primary)] sm:self-auto"
          >
            {t('landing.routes.cta')}
            <span className="ml-2" aria-hidden="true">
              →
            </span>
          </Link>
        </div>
        <div className="pt-6">
          <RoutePreview routes={routes} error={routeError} />
        </div>
      </section>

      <section className="border-y border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)]">
        <div className="xr-page-container px-4 py-10 md:px-8 md:py-14">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--xr-text-muted)]">
              {t('landing.steps.eyebrow')}
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-[var(--xr-text-primary)] md:text-3xl">
              {t('landing.steps.title')}
            </h2>
          </div>
          <div className="mt-8 grid gap-px overflow-hidden rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-border-default)] md:grid-cols-3">
            {stepKeys.map((key, index) => (
              <article key={key} className="bg-[var(--xr-bg-surface)] p-6 md:min-h-52">
                <span className="text-xs font-semibold tabular-nums text-[var(--xr-brand-primary)]">
                  0{index + 1}
                </span>
                <h3 className="mt-8 text-lg font-semibold text-[var(--xr-text-primary)]">
                  {t(`landing.step.${key}.title`)}
                </h3>
                <p className="mt-3 text-sm leading-6 text-[var(--xr-text-secondary)]">
                  {t(`landing.step.${key}.body`)}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--xr-brand-primary)] text-[var(--xr-brand-primary-ink)]">
        <div className="xr-page-container flex flex-col gap-6 px-4 py-10 md:flex-row md:items-center md:justify-between md:px-8 md:py-12">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/65">
              {t('landing.finalCta.eyebrow')}
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
              {t('landing.finalCta.title')}
            </h2>
          </div>
          <Link
            to="/discover"
            className="inline-flex h-11 items-center self-start rounded-[var(--xr-radius-full)] bg-white px-5 text-sm font-semibold text-[#1a5a44] transition hover:bg-white/90 md:self-auto"
          >
            {t('landing.finalCta.action')}
          </Link>
        </div>
      </section>
    </div>
  )
}

function RoutePreview({
  routes,
  error,
}: {
  routes: RouteCardRoute[] | null
  error: string | null
}) {
  const { t } = useLocale()
  if (routes === null) {
    return (
      <div className="rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-5 text-sm text-[var(--xr-text-muted)]">
        {t('landing.routes.loading')}
      </div>
    )
  }
  if (error) {
    return (
      <div
        className="rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-5 text-sm text-[var(--xr-danger)]"
        data-testid="landing-route-error"
      >
        {t('landing.routes.error')}
      </div>
    )
  }
  if (routes.length === 0) {
    return (
      <div className="rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-5 text-sm text-[var(--xr-text-muted)]">
        {t('landing.routes.empty')}
      </div>
    )
  }
  return (
    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {routes.slice(0, 4).map((route) => (
        <RouteCard key={route.id} route={route} showFavoriteHeart={false} interactive={false} />
      ))}
    </ul>
  )
}
